// © Microsoft Corporation. All rights reserved.

import { CombinedObjectTelemetryContract, ObjectTelemetryContractInternal } from "../types/TelemetryInternalContract";
import { ObjectTelemetryContract, TelemetryAttrName, TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { closestElement, dispatchTelemetryEvent, getAllTelemetryElements, getHeadline } from "../utilities/TelemetryHelper";

import { TelemetryContractCoverter } from "./TelemetryContractCoverter";
import { TelemetryObject } from "./TelemetryObject";
import { merge } from "lodash-es";

/**
 * Object metadata class which combination of external contract and internal data
 * @class TelemetryObject
 */
export class TelemetryDOM {
    /**
     * Flag specified whether layout need to be updated.
     */
    private static elementMap: WeakMap<Element, TelemetryDOM> = new WeakMap();

    /**
     * Root object.
     */
    private static root: TelemetryDOM = new TelemetryDOM(
        { name: "root" }
    );

    /**
     * Last id.
     */
    private static lastId = 0;

    /**
     * Flag specified whether layout need to be updated.
     */
    private static needUpdate = false;

    // #region Public properties
    /**
     * External object contract.
     */
    public contract: ObjectTelemetryContract;

    /**
     * Reference to children's object.
     */
    public readonly children: TelemetryDOM[];

    /**
     * Flag for identifying whether object already is captured or not.
     */
    public isCaptured?: boolean;

    /**
     * Flag for identifying whether object is visible or not.
     */
    public isVisible?: boolean;
    // #endregion

    // #region Private properties
    /**
     * Ordinal of the object.
     */
    private element;

    /**
     * Id of current object.
     */
    private id: number;

    /**
     * Ordinal of the object.
     */
    private ordinal = 0;

    /**
     * Last ordinal of its children.
     */
    private ordinalCount = 0;

    /**
     * Reference to parent object in dom.
     */
    private parentDOM: TelemetryDOM;
    // #endregion

    /**
     * Contract getter.
     * @returns Combined contract between internal and external.
     */
    public get combinedContract(): CombinedObjectTelemetryContract {
        if (this.contract) {
            const headline = (this.contract.content = this.contract.content || {}).headline;
            this.contract.content.headline = headline || getHeadline(this.element);
        }

        return merge({}, this.contract, this.internalContract);
    }

    /**
     * Internal contract getter.
     * @returns Internal contract.
     */
    private get internalContract(): ObjectTelemetryContractInternal {
        const { lineage, lineageOrdinal } = TelemetryDOM.getLineage(this);
        const contract = {
            placement: {
                id: this.id,
                lineage: lineage,
                lineageOrdinal: lineageOrdinal
            }
        } as ObjectTelemetryContractInternal;

        if (this.parentDOM && this.parentDOM.id && this.parentDOM.id !== 0) {
            contract.placement.parentId = this.parentDOM.id;
            contract.placement.ordinal = this.ordinal;
        }

        return contract;
    }

    constructor(contract: ObjectTelemetryContract) {
        if (contract) {
            this.contract = contract;
        } else {
            this.contract = {} as any;
        }

        this.ordinalCount = 0;
    }

    // #region Public static functions
    /**
     * Add new element to telemetry tree struture.
     * @params element Element being added.
     */
    public static addElement(element: Element) {
        if (!element) {
            return;
        }

        const contract = this.parseContract(element);
        let obj = this.get(element);

        // create object id for newly added element.
        if (!obj || !obj.id) {
            obj = new TelemetryDOM(contract);
            obj.id = this.generateId();
            this.set(element, obj);
        } else {
            obj.contract = contract;
        }

        if (!obj) {
            return;
        }

        let parentElement = element.parentElement;

        // In case the parent is shadow root.
        if (!parentElement && element.parentNode && (element.parentNode as ShadowRoot).host) {
            parentElement = (element.parentNode as ShadowRoot).host as HTMLElement;
        }

        let parent = parentElement && closestElement(parentElement, "[" + TelemetryAttrName + "]");
        let parentDOMObj = parent && this.get(parent);

        if (parent && !parentDOMObj) {
            dispatchTelemetryEvent("telemetrynodeadded", parent);
            parentDOMObj = this.get(parent);
        }

        if (obj === parentDOMObj) {
            parent = parent.closest("[" + TelemetryAttrName + "]");
            parentDOMObj = this.get(parent);
        }

        obj.parentDOM = parentDOMObj && parentDOMObj !== obj ? parentDOMObj : TelemetryDOM.getRoot();
        this.needUpdate = true;

        return obj;
    }

    /**
     * Render telemetry attribute from the telemetry object.
     * @param telemetryObject Telemetry object.
     * @returns Telemetry attribute.
     */
    public static renderObject(telemetryObject: TelemetryObject): any {
        return telemetryObject.render();
    }

    /**
     * Render telemetry attribute from the contract.
     * @param contract Telemetry contract.
     * @returns Telemetry attribute.
     */
    public static renderContract(contract: ObjectTelemetryContract): any {
        return TelemetryObject.render(contract);
    }

    /**
     * Set telemetry attribute.
     * @param element html element.
     * @param contract telemetry contract.
     */
    public static setAttribute(element: HTMLElement, contract: ObjectTelemetryContract, object: TelemetryObjectBase = null): void {
        if (!element || !element.setAttribute) {
            return;
        }

        contract = contract || (object && object.contract);
        const attr = this.renderContract(contract);
        if (attr) {
            element.setAttribute(TelemetryAttrName, attr[TelemetryAttrName]);
        }
    }

    /**
     * Get telemetry dom object from an element.
     * @param element The element.
     * @returns Telemetry object.
     */
    public static get(element: Element): TelemetryDOM {
        const telemetryDOM = this.elementMap.get(element);
        const updatedContract = this.parseContract(element);
        if (telemetryDOM && updatedContract) {
            telemetryDOM.contract = updatedContract;
        }

        return telemetryDOM;
    }

    /**
     * Get lineage and lineageOrdinal strings.
     * Lineage is concatenation of telemetry object's name of the current object up to the root. It basically tells the placement of the object in the page.
     * Lineage ordinal is concatenation of ordinal. It tells which position of each object up to root in the hierachy is.
     * Lineage can be duplicated since object name can be the same in some cases e.g. slideshow. Lineage ordinal is used to distinguish that.
     * @params obj Target telemetry object.
     * @returns Lineage and lineageOrdinal strings.
     */
    public static getLineage(obj: TelemetryDOM, delimeter = ">", includeRootElement = false): any {
        if (obj.parentDOM && obj.parentDOM !== TelemetryDOM.root) {
            let { lineage, lineageOrdinal } = this.getLineage(obj.parentDOM, delimeter, includeRootElement);
            lineage += delimeter + obj.contract.name;
            const newLineageOrdinal = (lineageOrdinal ? lineageOrdinal + delimeter : "") + obj.ordinal;
            return { lineage, lineageOrdinal: newLineageOrdinal };
        } else if (obj.parentDOM && obj.parentDOM === TelemetryDOM.root && includeRootElement) {
            const outputObj: any = { lineage: obj.contract.name };
            outputObj.lineageOrdinal = "" + obj.ordinal;
            return outputObj;
        } else {
            return { lineage: obj.contract.name };
        }
    }

    /**
     * Get root object
     * @returns Telemetry root object.
     */
    public static getRoot(): TelemetryDOM {
        return TelemetryDOM.root;
    }

    /**
     * Get telemetryObject
     * @returns Telemetry root object.
     */
    public static getTelemetryObject(element: HTMLElement): TelemetryObject {
        const obj = this.get(element);
        if (obj && obj.contract) {
            return new TelemetryObject(obj.contract);
        }
    }

    /**
     * Get telemetry contract from an element.
     * @param element The element.
     * @returns Telemetry object.
     */
    public static parseContract(element: Element): ObjectTelemetryContract {
        try {
            const metadataAttribute = element && element.getAttribute && element.getAttribute(TelemetryAttrName);
            return this.parseAttribute(metadataAttribute);
        } catch {
            // ignore json parsing error
            console.log("json parse error");
        }
    }

    /**
     * Get telemetry contract from a metadata string.
     * @param metadataAttribute Metadata string
     * @returns Telemetry object.
     */
    public static parseAttribute(metadataAttribute: string): ObjectTelemetryContract {
        try {
            const jsonObj = metadataAttribute && JSON.parse(metadataAttribute);
            return this.parseMetadata(jsonObj);
        } catch {
            // ignore json parsing error
        }
    }

    /**
     * Get telemetry contract from a metadata object.
     * @param metadata Metadata object
     * @returns Telemetry object.
     */
    public static parseMetadata(metadata: any): ObjectTelemetryContract {
        try {
            return metadata && TelemetryContractCoverter.decompress(metadata) as ObjectTelemetryContract;
        } catch {
            // ignore json parsing error
        }
    }

    /**
     * Set telemetry dom object from an element.
     * @param element The element.
     * @param domObj Telemetry dom object.
     */
    public static set(element: Element, domObj: TelemetryDOM): void {
        domObj.element = element;
        this.elementMap.set(element, domObj);
    }

    /**
     * Update layout.
     */
    public static updateLayout(): void {
        if (!this.needUpdate) {
            return;
        }

        this.needUpdate = false;
        this.root.ordinalCount = 0;

        const elems = getAllTelemetryElements(document.documentElement);
        for (let i = 0; i < elems.length; i++) {
            const elem = elems[i];
            let obj = this.get(elem);
            if (!obj || obj.parentDOM === undefined) {
                // there is some cases parentDOM is not available since element does not get added to the memory yet (updateLayout get called before addElement).
                // so, we execute addElement here to ensure that.
                dispatchTelemetryEvent("telemetrynodeadded", elem);
                obj = this.get(elem);
            }

            obj.ordinal = ++obj.parentDOM.ordinalCount;
            obj.ordinalCount = 0;
        }
    }

    /**
     * Create object id.
     * @param Object id.
     */
    private static generateId(): number {
        const id = ++this.lastId;

        return id;
    }
    // #endregion
}